import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Zap, HelpCircle } from 'lucide-react';

const PlanDetailModal = ({ pkg, billingCycle, onClose, onSubscribe }) => {
    if (!pkg) return null;

    // Helper to extract numeric price and calculate yearly billing display
    const getPriceData = () => {
        const getNum = (str) => parseInt((str || '').replace(/[^\d]/g, '') || '0');
        const monthlyVal = getNum(pkg.price);
        
        if (billingCycle === 'yearly') {
            const displayPrice = pkg.yearly || `₹${(monthlyVal * 12).toLocaleString('en-IN')}`;
            return { price: displayPrice, period: pkg.yearly ? '' : '/Year' };
        }
        return { price: pkg.price, period: pkg.period || '/Month' };
    };

    const priceData = getPriceData();

    return (
        <AnimatePresence>
            <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50000, padding: '20px' }}>
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.75)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                    }}
                />

                {/* Modal Container */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ type: 'spring', duration: 0.5, bounce: 0.15 }}
                    style={{
                        position: 'relative',
                        background: '#0a0a0a',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '24px',
                        width: '100%',
                        maxWidth: '550px',
                        maxHeight: '85vh',
                        overflowY: 'auto',
                        padding: '32px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(245, 158, 11, 0.05)',
                        color: '#fff',
                        fontFamily: '"Outfit", sans-serif',
                        zIndex: 1,
                    }}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            color: '#fff',
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'; }}
                    >
                        <X size={18} />
                    </button>

                    {/* Badge if available */}
                    {pkg.badge && (
                        <div style={{ display: 'inline-block', background: '#f59e0b', color: '#000', padding: '4px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '16px', letterSpacing: '0.5px' }}>
                            {pkg.badge}
                        </div>
                    )}

                    {/* Header */}
                    <div style={{ marginBottom: '24px' }}>
                        <h2 style={{ fontSize: '28px', fontWeight: '900', margin: '0 0 6px 0', textTransform: 'uppercase', color: '#fff' }}>
                            {pkg.name}
                        </h2>
                        {pkg.subtitle && (
                            <p style={{ fontSize: '14px', color: '#f59e0b', fontWeight: '700', margin: '0 0 12px 0', textTransform: 'uppercase' }}>
                                {pkg.subtitle}
                            </p>
                        )}
                        <p style={{ fontSize: '15px', color: '#9ca3af', lineHeight: '1.6', margin: 0 }}>
                            {pkg.description}
                        </p>
                    </div>

                    {/* Price Block */}
                    <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '16px', padding: '20px', marginBottom: '28px', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <span style={{ fontSize: '42px', fontWeight: '900', color: '#fff' }}>{priceData.price}</span>
                        <span style={{ fontSize: '16px', color: '#9ca3af', fontWeight: '700' }}>{priceData.period}</span>
                    </div>

                    {/* Features List */}
                    <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ fontSize: '13px', fontWeight: '800', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '8px' }}>
                            What's Included
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {(pkg.features || []).map((feature, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'start', opacity: feature.included ? 1 : 0.35 }}>
                                    <div style={{ marginRight: '12px', marginTop: '2px', flexShrink: 0 }}>
                                        {feature.included ? (
                                            <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Check size={12} color="#000" strokeWidth={3} />
                                            </div>
                                        ) : (
                                            <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <X size={12} color="#9ca3af" />
                                            </div>
                                        )}
                                    </div>
                                    <span style={{ fontSize: '14px', color: feature.included ? '#fff' : '#9ca3af', fontWeight: feature.included ? '600' : '400', lineHeight: '1.4' }}>
                                        {feature.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA Button */}
                    <button
                        onClick={() => onSubscribe(pkg)}
                        style={{
                            width: '100%',
                            padding: '16px',
                            borderRadius: '14px',
                            border: 'none',
                            background: '#f59e0b',
                            color: '#000',
                            fontWeight: '900',
                            fontSize: '16px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            fontFamily: '"Outfit", sans-serif',
                            boxShadow: '0 8px 20px rgba(245, 158, 11, 0.25)',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 12px 24px rgba(245, 158, 11, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'none';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 158, 11, 0.25)';
                        }}
                    >
                        <span>CHOOSE THIS PLAN</span>
                        <Zap size={16} fill="currentColor" />
                    </button>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default PlanDetailModal;
